import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Card,
  CardContent,
  Divider,
  Avatar,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import Popup from '../../base-components/Popup';
import { getSessionUser, hasPermission } from '../../utils/roleAccess';

const validationSchema = Yup.object({
  company_name: Yup.string().required('Company name is required'),
  company_email: Yup.string()
        .email('Invalid email format')
        .required('Company email is required'),
});

const BASE_URL = process.env.REACT_APP_BASE_URL;

const CompanyProfileForm = () => {
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(null);
  const navigate = useNavigate();
  const [photoPreview, setPhotoPreview] = useState(null);
  const fileInputRef = useRef();
  const [isPopupOpen , setIsPopupOpen] = useState(false);
  const [isSuccessAlert , setIsSuccessAlert] = useState(false);
  const [isFailureAlert , setIsFailureAlert] = useState(false);
  const [successMessage , setSuccessMessage] = useState('');
  const [failureMessage , setFailureMessage] = useState('');

    useEffect(()=> {
      const sessionStr = sessionStorage.getItem('user-info');
  
      if(sessionStr){
        const session = JSON.parse(sessionStr);
        setCompanyId(session.user.company_id);
      }
       else {
        navigate('/login');
      }
    }, [])

  const fetchCompanyDetails = async () => {
   try {
     const response = await fetch(`${BASE_URL}company/${companyId}`);
     const data = await response.json();
     if (!response.ok) {
       throw new Error(data.message || 'Failed to fetch company details');
     }
     if(data.company_logo && typeof data.company_logo === 'string') {
       setPhotoPreview(`${BASE_URL}${data.company_logo}`);
     }
     setInitialValues({
       ...data,
       allow_voucher_creation: !!data.allow_voucher_creation,
       allow_voucher_installments: !!data.allow_voucher_installments,
     });
   } catch (error) {
     console.error('Failed to fetch company details:', error);
   }
  }

  useEffect(() => {
    if (companyId) {
      fetchCompanyDetails();
      setLoading(false);
    } else {
      console.error('Company ID is not available');
      setLoading(false);
    }
  }, [companyId]);

  const handleUpdateCompany = async (values) => {
    try {
      const formData = new FormData();
      formData.append('company_name', values.company_name);
      formData.append('company_email', values.company_email);
      formData.append('allow_voucher_creation', values.allow_voucher_creation ? 'true' : 'false');
      formData.append('allow_voucher_installments', values.allow_voucher_installments ? 'true' : 'false');
      if (values.company_logo instanceof File) {
        formData.append('company_logo', values.company_logo);
      }
      const response = await fetch(`${BASE_URL}company/${companyId}`, {
        method: 'PUT',
        body: formData,
      });
      if (!response.ok) {
        throw new Error('Failed to update company');
      }
      setIsPopupOpen(true);
      setIsSuccessAlert(true);
      setSuccessMessage('Company updated successfully');
      setTimeout(() => {
        setIsPopupOpen(false);
        setIsSuccessAlert(false);
        setSuccessMessage('');
      }, 5000);
    } catch (error) {
    setIsFailureAlert(true);
    setFailureMessage('Failed to update company');
    setIsPopupOpen(true);
    setTimeout(() => {
      setIsPopupOpen(false);
      setIsSuccessAlert(false);
      setIsFailureAlert(false);
      setSuccessMessage('');
      setFailureMessage('');
    }, 2000);
      console.error('Failed to update company:', error);
    }
  }

  const handlePhotoSelect = () => {
    fileInputRef.current?.click();
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialValues || {
      company_name: '',
      company_email: '',
      allow_voucher_creation: false,
      allow_voucher_installments: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        await handleUpdateCompany(values);
      } catch (err) {
        console.error('Update failed:', err);
      }
    },
  });

  if (loading || !initialValues) {
    return (
      <Box display="flex" justifyContent="center" py={10}>
        <CircularProgress />
      </Box>
    );
  }

return (
    <>
    <Card elevation={4} sx={{ maxWidth: 700, mx: 'auto', p: 3, borderRadius: 3 }}>
      <CardContent>
        <Typography variant="h5" fontWeight={600} gutterBottom textAlign={'center'} sx={{ mb: 3, color: '#D71920' }}>
          Update Company Profile
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <form onSubmit={formik.handleSubmit}>
          {/* Logo + Upload */}
          <Box display="flex" alignItems="center" justifyContent="center" flexDirection="column" gap={1} mb={5}>
            <Avatar
              src={photoPreview}
              alt="Company Logo"
              variant="rounded"
              sx={{ width: 120, height: 120, borderRadius: 2, mb: 2, boxShadow: 3, p: 1, bgcolor: '#f5f5f5' }}
            />
            <Button variant="outlined" size="small" onClick={handlePhotoSelect}>
              Select Logo
            </Button>
            <input
              type="file"
              accept="image/*"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={(e) => {
                const file = e.target.files[0];
                if (file) {
                  formik.setFieldValue('company_logo', file);
                  const reader = new FileReader();
                  reader.onloadend = () => setPhotoPreview(reader.result);
                  reader.readAsDataURL(file);
                }
              }}
            />
            
          </Box>

          <Box mb={5}>
            <TextField
              fullWidth
              label="Company Name"
              name="company_name"
              value={formik.values.company_name}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.company_name && Boolean(formik.errors.company_name)}
              helperText={formik.touched.company_name && formik.errors.company_name}
            />
          </Box>

          <Box mb={5}>
            <TextField
              fullWidth
              label="Company Email"
              name="company_email"
              value={formik.values.company_email}
              InputProps={{ readOnly: true }}
            />
          </Box>

          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formik.values.allow_voucher_creation}
                  onChange={(e) =>
                    formik.setFieldValue('allow_voucher_creation', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Allow admin voucher creation"
            />
          </Box>

          <Box mb={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!formik.values.allow_voucher_installments}
                  onChange={(e) =>
                    formik.setFieldValue('allow_voucher_installments', e.target.checked)
                  }
                  color="primary"
                />
              }
              label="Allow splitting vouchers into installments"
            />
          </Box>

          {hasPermission(getSessionUser(), 'settings:update') && (
          <Box textAlign="center" mt={3}>
            <Button type="submit" variant="contained" color="primary" size="large" sx={{ px: 4 }} fullWidth>
              Save Changes
            </Button>
          </Box>
          )}
        </form>
      </CardContent>
    </Card>
    {
        isPopupOpen && (
          <Popup 
          onClose={() => setIsPopupOpen(false)}
          alertType={isSuccessAlert ? 'success' : isFailureAlert ? 'failure' : ''}
          message={isSuccessAlert ? successMessage : isFailureAlert ? failureMessage : ''}
          />
        )
      }
    </>
  );
};

export default CompanyProfileForm;
